import * as THREE from 'three';
import { Utils } from '../utils.js';
import { EventDispatcher } from '../EventDispatcher.js';
import { Measure } from './Measure.js';
import { truncate } from 'lodash';

export class MeasurementPlaneTool extends EventDispatcher {
    constructor(viewer) {
        super();
        this.viewer = viewer;
        this.renderer = viewer.renderer;
        this.scene = new THREE.Scene();
        this.scene.name = 'scene_plane';
        this.viewer.inputHandler.registerInteractiveScene(this.scene);
        this.activePlane = null;
        this.inserting = false;
        this.ifcHelper = null;
        this.activeMeasure = null;
        this.lastMeasureValid = false;
        viewer.addEventListener('update', this.update.bind(this));
        viewer.addEventListener('render.pass.scene', (e) => this.render(e));
        this.tempColor = new THREE.Color(0xff0000);
    }

    getNearbyPoints(center, radius) {
        const points = [];
        for (const pc of this.viewer.scene.pointclouds) {
            const result = pc.getPointsWithinRadius(center, radius);
            if (result) {
                points.push(...result);
            }
        }
        return points;
    }

    getAlignedPoints(points, intersectionPoint, cameraPosition, width, length) {
        const alignedPoints = [];
        const viewDir = new THREE.Vector3().subVectors(intersectionPoint, cameraPosition).normalize();
        for (const p of points) {
            const v = new THREE.Vector3().subVectors(p.position, intersectionPoint);
            const proj = v.dot(viewDir);
            const perpendicular = new THREE.Vector3().subVectors(v, viewDir.clone().multiplyScalar(proj));
            if (Math.abs(proj) <= length / 2 && perpendicular.length() <= width / 2) {
                alignedPoints.push(p);
            }
        }
        return alignedPoints;
    }

    computeBestFitPlanePoc(points, cameraPosition) {
        // Added cameraPosition parameter
        const mean = new THREE.Vector3();
        points.forEach((p) => mean.add(p.position));
        mean.divideScalar(points.length);

        let covariance = [
            [0, 0, 0],
            [0, 0, 0],
            [0, 0, 0],
        ];

        for (const p of points) {
            const diff = new THREE.Vector3().subVectors(p.position, mean);
            covariance[0][0] += diff.x * diff.x;
            covariance[0][1] += diff.x * diff.y;
            covariance[0][2] += diff.x * diff.z;
            covariance[1][0] += diff.y * diff.x;
            covariance[1][1] += diff.y * diff.y;
            covariance[1][2] += diff.y * diff.z;
            covariance[2][0] += diff.z * diff.x;
            covariance[2][1] += diff.z * diff.y;
            covariance[2][2] += diff.z * diff.z;
        }

        const eig = numeric.eig(covariance);
        const eigenvalues = eig.lambda.x;
        const eigenvectors = eig.E.x;

        // Find the index of the smallest eigenvalue
        let minIndex = 0;
        for (let i = 1; i < eigenvalues.length; i++) {
            if (eigenvalues[i] < eigenvalues[minIndex]) {
                minIndex = i;
            }
        }

        // Extract the corresponding eigenvector
        const normal = new THREE.Vector3(eigenvectors[0][minIndex], eigenvectors[1][minIndex], eigenvectors[2][minIndex]).normalize();

        // Adjust normal direction to face towards the camera
        const toCamera = new THREE.Vector3().subVectors(cameraPosition, mean).normalize();
        if (normal.dot(toCamera) < 0) {
            normal.negate();
        }

        return normal;
    }

    computeBestFitNormalMesh(mesh, faceIndex, cameraPosition) {
        if (!mesh?.geometry?.attributes?.position) {
            return;
        }

        const geometry = mesh.geometry;
        const position = geometry.attributes.position;

        // Best path for normal THREE.Mesh:
        // use the exact triangle hit by the raycast.
        if (mesh.constructor.name === 'Mesh' && faceIndex !== undefined && faceIndex !== null) {
            let aIndex, bIndex, cIndex;

            if (geometry.index) {
                const index = geometry.index;
                const base = faceIndex * 3;

                if (base + 2 >= index.count) {
                    return;
                }

                aIndex = index.getX(base);
                bIndex = index.getX(base + 1);
                cIndex = index.getX(base + 2);
            } else {
                const base = faceIndex * 3;

                if (base + 2 >= position.count) {
                    return;
                }

                aIndex = base;
                bIndex = base + 1;
                cIndex = base + 2;
            }

            const a = new THREE.Vector3().fromBufferAttribute(position, aIndex).applyMatrix4(mesh.matrixWorld);
            const b = new THREE.Vector3().fromBufferAttribute(position, bIndex).applyMatrix4(mesh.matrixWorld);
            const c = new THREE.Vector3().fromBufferAttribute(position, cIndex).applyMatrix4(mesh.matrixWorld);

            const cb = new THREE.Vector3().subVectors(c, b);
            const ab = new THREE.Vector3().subVectors(a, b);

            const normal = new THREE.Vector3().crossVectors(cb, ab);

            if (normal.lengthSq() < 1e-12) {
                return;
            }

            normal.normalize();

            if (cameraPosition) {
                const triangleCenter = new THREE.Vector3()
                    .addVectors(a, b)
                    .add(c)
                    .multiplyScalar(1 / 3);

                const toCamera = new THREE.Vector3().subVectors(cameraPosition, triangleCenter).normalize();

                if (normal.dot(toCamera) < 0) {
                    normal.negate();
                }
            }

            return normal;
        }

        // Keep your existing IFC behavior mostly unchanged.
        let worldPositions = [];
        let vertexCount = 0;

        if (mesh.constructor.name === 'IFCModel') {
            const tempExpressID = this.ifcHelper.ifcManager.getExpressId(geometry, faceIndex || 0);

            const positions = geometry.attributes.position.array;
            const indices = this.filterIndices([tempExpressID], geometry);

            vertexCount = indices.length;

            for (let i = 0; i < vertexCount; i++) {
                const idx = indices[i];
                worldPositions.push(
                    new THREE.Vector3(positions[idx * 3], positions[idx * 3 + 1], positions[idx * 3 + 2]).applyMatrix4(mesh.matrixWorld)
                );
            }
        }

        if (worldPositions.length < 3) {
            return;
        }

        const mean = new THREE.Vector3();
        worldPositions.forEach((pos) => mean.add(pos));
        mean.divideScalar(worldPositions.length);

        let covariance = [
            [0, 0, 0],
            [0, 0, 0],
            [0, 0, 0],
        ];

        for (const pos of worldPositions) {
            const diff = new THREE.Vector3().subVectors(pos, mean);
            covariance[0][0] += diff.x * diff.x;
            covariance[0][1] += diff.x * diff.y;
            covariance[0][2] += diff.x * diff.z;
            covariance[1][0] += diff.y * diff.x;
            covariance[1][1] += diff.y * diff.y;
            covariance[1][2] += diff.y * diff.z;
            covariance[2][0] += diff.z * diff.x;
            covariance[2][1] += diff.z * diff.y;
            covariance[2][2] += diff.z * diff.z;
        }

        const eig = numeric.eig(covariance);
        const eigenvalues = eig.lambda.x;
        const eigenvectors = eig.E.x;

        let minIndex = 0;
        for (let i = 1; i < eigenvalues.length; i++) {
            if (eigenvalues[i] < eigenvalues[minIndex]) {
                minIndex = i;
            }
        }

        const normal = new THREE.Vector3(eigenvectors[0][minIndex], eigenvectors[1][minIndex], eigenvectors[2][minIndex]).normalize();

        if (cameraPosition) {
            const toCamera = new THREE.Vector3().subVectors(cameraPosition, mean).normalize();
            if (normal.dot(toCamera) < 0) {
                normal.negate();
            }
        }

        return normal;
    }

    computeFragmentNormal(fragmentHit, cameraPosition) {
        if (!fragmentHit) return null;

        const hitPoint = fragmentHit.point;
        if (!hitPoint || !cameraPosition) return null;

        // 1. Best path: ThatOpen fragments raycast result may already provide the normal.
        if (fragmentHit.normal?.isVector3) {
            const normal = fragmentHit.normal.clone().normalize();

            if (normal.lengthSq() < 1e-12) return null;

            const toCamera = new THREE.Vector3().subVectors(cameraPosition, hitPoint).normalize();

            if (normal.dot(toCamera) < 0) {
                normal.negate();
            }

            return normal;
        }

        // 2. Good path: ThatOpen fragments raycast result may provide the 3 face points.
        // These are much better than trying to re-raycast children.
        if (fragmentHit.facePoints && fragmentHit.facePoints.length >= 9) {
            const fp = fragmentHit.facePoints;

            const a = new THREE.Vector3(fp[0], fp[1], fp[2]);
            const b = new THREE.Vector3(fp[3], fp[4], fp[5]);
            const c = new THREE.Vector3(fp[6], fp[7], fp[8]);

            const cb = new THREE.Vector3().subVectors(c, b);
            const ab = new THREE.Vector3().subVectors(a, b);

            const normal = new THREE.Vector3().crossVectors(cb, ab);

            if (normal.lengthSq() < 1e-12) return null;

            normal.normalize();

            const toCamera = new THREE.Vector3().subVectors(cameraPosition, hitPoint).normalize();

            if (normal.dot(toCamera) < 0) {
                normal.negate();
            }

            return normal;
        }

        // 3. Fallback: re-cast a THREE.Raycaster against the fragment object's child
        //    InstancedMeshes from just behind the hit point.  This recovers face.normal
        //    when the OBC GPU raycaster did not populate .normal or .facePoints.
        const fragObject = fragmentHit.object;
        if (!fragObject) return null;

        const meshChildren = [];
        fragObject.traverse((child) => {
            if (child.visible && (child.isMesh || child.isInstancedMesh)) {
                meshChildren.push(child);
            }
        });

        if (meshChildren.length === 0) return null;

        const rayDir = new THREE.Vector3().subVectors(hitPoint, cameraPosition).normalize();
        // Start 5 mm behind the surface so the ray definitely hits the face.
        const rayOrigin = hitPoint.clone().sub(rayDir.clone().multiplyScalar(0.005));
        const reRaycaster = new THREE.Raycaster(rayOrigin, rayDir, 0, 0.05);
        const hits = reRaycaster.intersectObjects(meshChildren, false);

        for (const hit of hits) {
            if (!hit.face) continue;
            const normal = hit.face.normal.clone();

            // For InstancedMesh each instance may have its own transform that must
            // be applied on top of the mesh's matrixWorld before we can go to world space.
            let worldMatrix = hit.object.matrixWorld.clone();
            if (hit.object.isInstancedMesh && hit.instanceId !== undefined) {
                const instanceMatrix = new THREE.Matrix4();
                hit.object.getMatrixAt(hit.instanceId, instanceMatrix);
                worldMatrix = new THREE.Matrix4().multiplyMatrices(worldMatrix, instanceMatrix);
            }

            const normalMatrix = new THREE.Matrix3().getNormalMatrix(worldMatrix);
            normal.applyMatrix3(normalMatrix).normalize();

            if (normal.lengthSq() < 1e-12) continue;

            const toCamera = new THREE.Vector3().subVectors(cameraPosition, hitPoint).normalize();
            if (normal.dot(toCamera) < 0) normal.negate();
            return normal;
        }

        return null;
    }

    filterIndices(ids, geometry) {
        const expressIDAttr = geometry.getAttribute('expressID');
        const indexArray = geometry.index.array;
        const resultIndices = [];
        const idSet = new Set(ids);

        for (let i = 0; i < indexArray.length; i += 3) {
            const a = indexArray[i],
                b = indexArray[i + 1],
                c = indexArray[i + 2];
            if (idSet.has(expressIDAttr.getX(a)) || idSet.has(expressIDAttr.getX(b)) || idSet.has(expressIDAttr.getX(c))) {
                resultIndices.push(a, b, c);
            }
        }

        return resultIndices;
    }

    startInsertion(args = {}, finishCallback, cancelCallback) {
        this.ifcHelper = args.ifcHelper || null;
        const width = args.width || 0.5;
        const height = args.height || 0.5;
        const color = args.color || 0x00ff00;
        const opacity = args.opacity || 0.5;
        const geometry = new THREE.PlaneGeometry(width, height);
        const material = new THREE.MeshBasicMaterial({
            color: color,
            transparent: true,
            opacity: opacity,
            side: THREE.DoubleSide,
        });

        const plane = new THREE.Mesh(geometry, material);
        plane.name = args.name || 'Plane';
        this.dispatchEvent({ type: 'start_inserting_plane', plane: plane });
        plane.visible = false;
        this.scene.add(plane);
        this.activePlane = plane;
        this.inserting = true;

        const calibrating = args.calibrating || false;
        const measure = new Measure(this.viewer, calibrating, args.ifcHelper);
        this.viewer.scene.addMeasurement(measure);
        this.activeMeasure = measure;
        this.lastMeasureValid = false;
        measure.visible = false;

        let cancel = { callback: null };
        let drop = (e) => {
            this.inserting = false;
            plane.removeEventListener('drop', drop);
            this.viewer.removeEventListener('cancel_insertions', cancel.callback);

            if (e.button === THREE.MOUSE.LEFT) {
                if (!this.lastMeasureValid) {
                    if (this.activeMeasure) {
                        this.viewer.scene.removeMeasurement(this.activeMeasure);
                        this.activeMeasure = null;
                    }
                    if (cancelCallback) cancelCallback();
                } else if (finishCallback && this.activeMeasure) {
                    this.viewer.measuringTool.measure = this.activeMeasure;
                    finishCallback();
                    if (cancelCallback) cancelCallback();
                }
            } else if (e.button === THREE.MOUSE.RIGHT) {
                if (cancelCallback && this.activeMeasure) {
                    this.viewer.scene.removeMeasurement(this.activeMeasure);
                    this.activeMeasure = null;
                    cancelCallback();
                }
            }
        };

        plane.addEventListener('drop', drop);
        this.viewer.addEventListener('cancel_insertions', cancel.callback);
        this.viewer.inputHandler.startDragging(plane);
        return plane;
    }

    async update() {
        if (this.inserting && this.activePlane) {
            const camera = this.viewer.scene.getActiveCamera();
            const mousePosition = this.viewer.inputHandler.mouse;
            if (!mousePosition) return;

            let I = Utils.getMousePointCloudIntersection(mousePosition, camera, this.viewer, this.viewer.scene.pointclouds, {
                pickClipped: true,
                pickInvisible: false,
            });

            if (!I && this.ifcHelper) {
                const ifcResult = await this.ifcHelper.castRayIfc();
                if (ifcResult) {
                    // fragHit preserves the full OBC RaycastResult so computeFragmentNormal
                    // can access .normal (world-space Vector3) and .facePoints (world-space
                    // Float32Array of the 3 triangle vertices) that OBC provides.
                    I = { location: ifcResult.point, object: ifcResult.object, faceIndex: ifcResult.faceIndex, fragHit: ifcResult };
                }
            }

            if (I) {
                this.activePlane.position.copy(I.location);

                const planeCenter = this.activePlane.position.clone();
                const planeWidth = this.activePlane.geometry.parameters.width;
                const planeHeight = this.activePlane.geometry.parameters.height;
                const searchRadius = Math.sqrt(planeWidth * planeWidth + planeHeight * planeHeight) / 2;
                let bestFitNormal;
                if (this.ifcHelper && I.object) {
                    if (I.object.isFrag && I.faceIndex === undefined) {
                        // Pass the full OBC RaycastResult as fragmentHit so the function
                        // can use .normal and .facePoints that OBC provides in world space.
                        bestFitNormal = this.computeFragmentNormal(I.fragHit, camera.position);
                    } else {
                        bestFitNormal = this.computeBestFitNormalMesh(I.object, I.faceIndex, camera.position);
                    }
                } else {
                    const nearbyPoints = this.getNearbyPoints(I.location, searchRadius);

                    if (nearbyPoints.length >= 3) {
                        const alignedPoints = this.getAlignedPoints(nearbyPoints, I.location, camera.position, planeWidth, planeHeight);

                        if (alignedPoints.length >= 3) {
                            bestFitNormal = this.computeBestFitPlanePoc(alignedPoints, camera.position);
                        }
                    }
                }

                if (bestFitNormal) {
                    const camDir = new THREE.Vector3().subVectors(camera.position, I.location);
                    const projected = camDir.clone().projectOnPlane(bestFitNormal).normalize();

                    if (projected.length() < 1e-3) {
                        projected.set(1, 0, 0).projectOnPlane(bestFitNormal).normalize();
                    }

                    const bitangent = new THREE.Vector3().crossVectors(bestFitNormal, projected).normalize();
                    const tangent = projected.clone();
                    const normal = bestFitNormal.clone();

                    const m = new THREE.Matrix4();
                    m.makeBasis(tangent, bitangent, normal);
                    this.activePlane.setRotationFromMatrix(m);
                }

                const normalDirection = new THREE.Vector3(0, 0, 1).applyQuaternion(this.activePlane.quaternion).normalize();

                let intersection;
                const startedFromIfc = !!I.fragHit;           // any IFC hit (fragment or mesh)
                const startedFromFragment = startedFromIfc && !!I.object?.isFrag; // OBC fragment specifically

                if (this.ifcHelper && startedFromIfc) {
                    let result;

                    if (startedFromFragment && this.ifcHelper.obcRaycaster) {
                        const offsetOrigin = planeCenter.clone().addScaledVector(normalDirection, 0.01);
                        result = await this.ifcHelper.castRayFromVectorAsync(offsetOrigin, normalDirection);
                    } else {
                        // Regular IFC mesh (GLB) — or fragment fallback without obcRaycaster
                        result = this.ifcHelper.castRayFromOrigin(planeCenter, normalDirection);
                    }

                    const point = result?.point;
                    if (point) {
                        intersection = { location: point };
                    }
                } else {
                    // Point-cloud start (or no ifcHelper) — end on point cloud
                    intersection = Utils.getRayPointCloudIntersection(
                        planeCenter,
                        normalDirection,
                        camera,
                        this.viewer,
                        this.viewer.scene.pointclouds,
                        { pickClipped: true, pickInvisible: false }
                    );
                }

                const measure = this.activeMeasure;
                if (intersection && measure) {
                    const distance = planeCenter.distanceTo(intersection.location);
                    if (distance > 0.001) {
                        measure.visible = true;
                        if (!measure.userData.added) {
                            measure.addMarker(planeCenter, this.tempColor);
                            measure.addMarker(intersection.location, this.tempColor);
                            measure.userData.added = true;
                        } else {
                            measure.setPosition(0, planeCenter);
                            measure.setPosition(1, intersection.location);
                        }
                        this.lastMeasureValid = true;
                    } else {
                        measure.visible = false;
                        this.lastMeasureValid = false;
                    }
                } else {
                    if (measure) measure.visible = false;
                    this.lastMeasureValid = false;
                }
            } else {
                const measure = this.activeMeasure;
                if (measure) measure.visible = false;
                this.lastMeasureValid = false;
            }
        }
    }

    render(params) {
        const renderer = this.renderer;
        const oldTarget = renderer.getRenderTarget();
        if (params.renderTarget) {
            renderer.setRenderTarget(params.renderTarget);
        }
        renderer.render(this.scene, this.viewer.scene.getActiveCamera());
        renderer.setRenderTarget(oldTarget);
    }
}


// Creating a simple origin-based cast is easy if you only mean a normal CPU ray through loaded meshes. You already have that shape with castRayFromVector and with your own Three.Raycaster logic.

// Creating the exact same behavior as the initial camera pick, but with an arbitrary origin and direction, is feasible only if you go below the current public API and add a new fragment-raycast path in the ThatOpen fragments layer. The blocker is that the current fragment path is not “camera ray except different origin”; it is a screen-space pipeline built around camera, mouse, and dom, and it also builds a frustum around the pointer region before sending the request to the fragment worker.

// So the practical answer is:

// App-side helper only: feasible, but not exact.
// Exact same behavior with no semantic downgrade: feasible, but you would need to patch or fork the ThatOpen fragments package and then expose a new method upward through FragmentsManager and SimpleRaycaster.
// Clean public API that already exists today: no.